import React from 'react';
import styled from 'styled-components';
import { 
  Scale, 
  Users, 
  FileText, 
  Gavel, 
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Shield,
  Building2,
  Landmark,
  Globe,
  Handshake,
  ChevronRight
} from 'lucide-react';

const PageContainer = styled.div`
  padding-top: 90px;
  min-height: 100vh;
  background: var(--background-light);
`;

const Hero = styled.section`
  background: linear-gradient(135deg, var(--primary-blue) 0%, #004d8f 100%);
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const HeroContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-family: var(--font-secondary);
  font-size: 3rem;
  color: var(--white);
  margin-bottom: 1rem;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 700px;
  margin: 0 auto;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem 4rem;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-top: -2rem;
  position: relative;
  z-index: 2;
`;

const ContentCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  padding: 2.5rem;
  border: 1px solid var(--border-light);
`;

const CardTitle = styled.h2`
  font-family: var(--font-secondary);
  font-size: 1.75rem;
  color: var(--primary-blue);
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--border-light);

  svg {
    color: var(--accent-gold);
  }
`;

const IntroText = styled.p`
  font-size: 1.1rem;
  color: var(--text-medium);
  line-height: 1.8;
  margin-bottom: 1.5rem;
`;

const HighlightBox = styled.div`
  background: linear-gradient(135deg, rgba(0, 38, 100, 0.05), rgba(254, 202, 0, 0.05));
  border-left: 4px solid var(--primary-blue);
  padding: 1.5rem;
  border-radius: 0 var(--radius-md) var(--radius-md) 0;
  margin-bottom: 2rem;

  p {
    font-size: 1rem;
    color: var(--text-dark);
    line-height: 1.8;
    margin-bottom: 1rem;

    &:last-child {
      margin-bottom: 0;
    }

    strong {
      color: var(--primary-blue);
    }
  }
`;

const MissionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const MissionCard = styled.div`
  background: var(--white);
  border: 2px solid var(--border-light);
  border-radius: var(--radius-lg);
  padding: 1.5rem;
  transition: var(--transition);

  &:hover {
    border-color: var(--accent-gold);
    transform: translateY(-4px);
    box-shadow: var(--shadow-lg);
  }
`;

const MissionIcon = styled.div`
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--white);
  margin-bottom: 1rem;
`;

const MissionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 0.5rem;
`;

const MissionText = styled.p`
  font-size: 0.95rem;
  color: var(--text-medium);
  line-height: 1.6;
`;

const ActionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ActionItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: var(--background-light);
  border-radius: var(--radius-md);
  border-left: 4px solid var(--accent-gold);

  svg {
    color: var(--accent-gold);
    flex-shrink: 0;
    margin-top: 2px;
  }

  span {
    font-size: 1rem;
    color: var(--text-dark);
    line-height: 1.6;
  }
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PowersCard = styled.div`
  background: linear-gradient(135deg, rgba(0, 150, 57, 0.08), rgba(0, 150, 57, 0.02));
  border: 2px solid rgba(0, 150, 57, 0.2);
  border-radius: var(--radius-lg);
  padding: 1.5rem;
  height: 100%;
`;

const LimitsCard = styled.div`
  background: linear-gradient(135deg, rgba(200, 16, 46, 0.08), rgba(200, 16, 46, 0.02));
  border: 2px solid rgba(200, 16, 46, 0.2);
  border-radius: var(--radius-lg);
  padding: 1.5rem;
  height: 100%;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;

  svg {
    color: ${props => props.$color || 'var(--primary-blue)'};
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 700;
    color: ${props => props.$color || 'var(--text-dark)'};
  }
`;

const ListItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 0;
  font-size: 0.95rem;
  color: var(--text-dark);
  line-height: 1.6;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  &:last-child {
    border-bottom: none;
  }

  svg {
    color: ${props => props.$iconColor || 'var(--accent-gold)'};
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const CannotDoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const CannotDoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: linear-gradient(135deg, rgba(200, 16, 46, 0.08), rgba(200, 16, 46, 0.02));
  border: 2px solid rgba(200, 16, 46, 0.15);
  border-radius: var(--radius-lg);

  svg {
    color: var(--secondary-red);
    flex-shrink: 0;
  }

  span {
    font-size: 0.95rem;
    color: var(--text-dark);
    font-weight: 500;
  }
`;

const CompetenceList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 1.5rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CompetenceItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--background-light);
  border-radius: var(--radius-md);

  svg {
    color: var(--primary-blue);
  }

  span {
    font-size: 0.9rem;
    color: var(--text-dark);
    font-weight: 500;
  }
`;

const RolesMissions = () => {
  return (
    <PageContainer>
      <Hero>
        <HeroContent>
          <HeroTitle>Rôles et Missions</HeroTitle>
          <HeroSubtitle>
            Découvrez les attributions, les pouvoirs et les limites de la Médiature de la République
          </HeroSubtitle>
        </HeroContent>
      </Hero>

      <Container>
        <ContentGrid>
          {/* Section Principale */}
          <ContentCard>
            <CardTitle>
              <Scale size={28} />
              Rôle et Missions
            </CardTitle>

            <HighlightBox>
              <p>
                La <strong>Médiature de la République</strong> est une <strong>autorité administrative indépendante</strong>, 
                investie d'une <strong>mission de service public de médiation</strong>.
              </p>
              <p>
                Elle est <strong>intercesseur entre l'Administration publique et les administrés</strong>.
              </p>
            </HighlightBox>

            <IntroText>
              La Médiature de la République reçoit les réclamations relatives au fonctionnement des 
              administrations de l'État, des collectivités autonomes, des établissements publics et 
              de tout organisme investi d'une mission de service public.
            </IntroText>

            <CompetenceList>
              <CompetenceItem>
                <Building2 size={20} />
                <span>Administrations de l'État</span>
              </CompetenceItem>
              <CompetenceItem>
                <Landmark size={20} />
                <span>Collectivités autonomes</span>
              </CompetenceItem>
              <CompetenceItem>
                <Globe size={20} />
                <span>Établissements publics</span>
              </CompetenceItem>
              <CompetenceItem>
                <Handshake size={20} />
                <span>Organismes de service public</span>
              </CompetenceItem>
            </CompetenceList>
          </ContentCard>

          {/* Actions */}
          <ContentCard>
            <CardTitle>
              <CheckCircle2 size={28} />
              Elle est appelée à
            </CardTitle>

            <ActionsList>
              <ActionItem>
                <Users size={22} />
                <span>Apporter une assistance aux citoyens pour faire valoir leurs droits</span>
              </ActionItem>
              <ActionItem>
                <FileText size={22} />
                <span>Instruire les réclamations liées aux dysfonctionnements des services publics</span>
              </ActionItem>
              <ActionItem>
                <Handshake size={22} />
                <span>Faire des suggestions pour un règlement à l'amiable des litiges</span>
              </ActionItem>
              <ActionItem>
                <Gavel size={22} />
                <span>Proposer au Président de la République des modifications législatives</span>
              </ActionItem>
              <ActionItem>
                <Shield size={22} />
                <span>Participer à l'amélioration des services publics et à la réconciliation nationale, la paix et la cohésion sociale</span>
              </ActionItem>
            </ActionsList>
          </ContentCard>

          {/* Pouvoirs et Limites */}
          <ContentCard>
            <CardTitle>
              <Scale size={28} />
              Pouvoirs et limites
            </CardTitle>

            <TwoColumnGrid>
              <PowersCard>
                <SectionHeader $color="var(--accent-green)">
                  <CheckCircle2 size={24} />
                  <h3>Ses pouvoirs</h3>
                </SectionHeader>

                <ListItem $iconColor="var(--accent-green)">
                  <CheckCircle2 size={18} />
                  <span>Se saisir d'office de toute question relevant de sa compétence</span>
                </ListItem>
                <ListItem $iconColor="var(--accent-green)">
                  <CheckCircle2 size={18} />
                  <span>Recevoir des alertes sur des situations de tension communautaire</span>
                </ListItem>
                <ListItem $iconColor="var(--accent-green)">
                  <CheckCircle2 size={18} />
                  <span>Mener toute mission confiée par le Président de la République</span>
                </ListItem>
              </PowersCard>

              <LimitsCard>
                <SectionHeader $color="var(--secondary-red)">
                  <XCircle size={24} />
                  <h3>Ses limites</h3>
                </SectionHeader>

                <p style={{fontSize: '0.9rem', color: 'var(--text-medium)', marginBottom: '1rem', fontWeight: 600}}>
                  La Médiature n'est pas compétente dans :
                </p>

                <ListItem $iconColor="var(--secondary-red)">
                  <XCircle size={18} />
                  <span>Les conflits entre personnes privées (physiques ou morales)</span>
                </ListItem>
                <ListItem $iconColor="var(--secondary-red)">
                  <XCircle size={18} />
                  <span>Les conflits impliquant des institutions à immunité internationale</span>
                </ListItem>
                <ListItem $iconColor="var(--secondary-red)">
                  <XCircle size={18} />
                  <span>Les conflits avec des administrations étrangères</span>
                </ListItem>
                <ListItem $iconColor="var(--secondary-red)">
                  <XCircle size={18} />
                  <span>Elle n'intervient pas dans une procédure judiciaire</span>
                </ListItem>
                <ListItem $iconColor="var(--secondary-red)">
                  <XCircle size={18} />
                  <span>Elle ne remet pas en cause une décision juridictionnelle</span>
                </ListItem>
              </LimitsCard>
            </TwoColumnGrid>

            {/* Ce qu'elle ne peut pas faire */}
            <div style={{marginTop: '2rem'}}>
              <SectionHeader $color="var(--secondary-red)" style={{justifyContent: 'center'}}>
                <AlertTriangle size={24} />
                <h3>La Médiature ne peut pas</h3>
              </SectionHeader>

              <CannotDoGrid>
                <CannotDoItem>
                  <XCircle size={24} />
                  <span>Imposer une décision à l'administration</span>
                </CannotDoItem>
                <CannotDoItem>
                  <XCircle size={24} />
                  <span>Annuler ou réformer une décision administrative</span>
                </CannotDoItem>
                <CannotDoItem>
                  <XCircle size={24} />
                  <span>Exiger une indemnisation</span>
                </CannotDoItem>
              </CannotDoGrid>
            </div>
          </ContentCard>
        </ContentGrid>
      </Container>
    </PageContainer>
  );
};

export default RolesMissions;
